import type { ConfiguratorState } from "./types";
import type { CpbChangePayload } from "../cpbBridge";
import { resolveRegistryHandler } from "./cpbSchemaRegistry";

type ChangeEntry = CpbChangePayload["changed"][number];
type Layer = CpbChangePayload["layers"][number];

const FALLBACK_WARNED = new Set<string>();
const UNKNOWN_WARNED = new Set<string>();

type EntryOutcome =
  | { kind: "patch"; patch: Partial<ConfiguratorState> }
  /**
   * The category shifted visibility (e.g. user switched designMode → CPB
   * fires `changed` for every now-hidden text/font input with `next: null`)
   * but carries no real value to mirror into `ConfiguratorState`. We return
   * "noop" so `mapCpbEntriesToPatch` can keep these out of the unmapped
   * list — they are not problems with our schema, they are CPB telling us
   * the form rerendered.
   */
  | { kind: "noop" }
  | { kind: "unmapped" };

/**
 * `entry.next == null` is CPB's signal that the option became invisible
 * (parent toggle hid it). It is never a content / config change we need
 * to forward into the viewer state.
 */
function isVisibilityToggle(entry: ChangeEntry): boolean {
  return entry.next == null;
}

/* ──────────────────────────────────────────────────────────────────────────
 * Public API
 * ──────────────────────────────────────────────────────────────────────── */

function classifyEntry(entry: ChangeEntry, layers: Layer[]): EntryOutcome {
  if (isVisibilityToggle(entry)) return { kind: "noop" };

  const resolved = resolveRegistryHandler(entry);

  if (resolved.kind === "id") {
    const patch = resolved.handler(entry, layers);
    return patch ? { kind: "patch", patch } : { kind: "noop" };
  }

  if (resolved.kind === "fallback") {
    const patch = resolved.handler(entry, layers);
    if (patch) {
      const key = entry.category.id || entry.category.title || "unknown";
      if (!FALLBACK_WARNED.has(key)) {
        FALLBACK_WARNED.add(key);
        console.warn(
          `%c[cpb-mapper] no ID handler for category "${entry.category.title}" (${key}); used fallback "${resolved.matchedBy}"`,
          "color:#e9a;",
        );
      }
      return { kind: "patch", patch };
    }
    /* Fallback matched the title/panel pair but the value was empty —
     * treat as noop, not unmapped. */
    return { kind: "noop" };
  }

  const unknownKey = `${entry.category.id}|${entry.category.title}|${entry.next?.label ?? ""}`;
  if (!UNKNOWN_WARNED.has(unknownKey)) {
    UNKNOWN_WARNED.add(unknownKey);
    console.warn(
      `%c[cpb-mapper] unknown CPB category — no ID/fallback match`,
      "color:#e9a;",
      {
        id: entry.category.id,
        title: entry.category.title,
        panel: entry.category.panelTitle,
        nextLabel: entry.next?.label,
        nextLayerValue: entry.next?.layerValue,
      },
    );
  }
  return { kind: "unmapped" };
}

export function mapCpbEntryToPatch(
  entry: ChangeEntry,
  layers: Layer[],
): Partial<ConfiguratorState> | null {
  const result = classifyEntry(entry, layers);
  return result.kind === "patch" ? result.patch : null;
}

/**
 * Fold multiple change entries into a single patch. Later entries override
 * earlier ones on overlap, matching `setState` merge order. Entries that
 * have no value to forward (visibility toggles, empty text inputs after a
 * design-mode switch, etc.) are silently dropped — only entries that
 * *should* have produced a patch but had no matching handler are returned
 * in `unmapped` so the dev console can flag genuine schema gaps.
 */
export function mapCpbEntriesToPatch(
  entries: ChangeEntry[],
  layers: Layer[] = [],
): { patch: Partial<ConfiguratorState>; unmapped: ChangeEntry[] } {
  const patch: Partial<ConfiguratorState> = {};
  const unmapped: ChangeEntry[] = [];
  for (const e of entries) {
    const result = classifyEntry(e, layers);
    if (result.kind === "patch") Object.assign(patch, result.patch);
    else if (result.kind === "unmapped") unmapped.push(e);
  }
  return { patch, unmapped };
}
